import { RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { RootRedirectPanel } from '@/components/root/root.redirect-panel';
import { AccountDetailsPanel } from '@/features/accounts/accounts.details-panel';
import { AccountsPanel } from '@/features/accounts/accounts.panel';
import { AuthDebugPanel } from '@/features/auth-debug/auth-debug.panel';
import { CachePanel } from '@/features/cache/cache.panel';
import { ErrorTestPanel } from '@/features/error-test/error-test.panel';
import { FeedbackPanel } from '@/features/feedback/feedback.panel';
import { ChecklistActivityPanel } from '@/features/checklists/checklist.activity-panel';
import { ChecklistCreatePanel } from '@/features/checklists/checklists.create-panel';
import { ChecklistDetailsPanel } from '@/features/checklists/ChecklistDetailsPanel';
import { ListRedirectPanel } from '@/features/lists/list.redirect-panel';
import { TemplateDetailsPanel } from '@/features/templates/TemplateDetailsPanel';
import { InheritancePanel } from '@/features/lists/InheritancePanel';
import { QuestionDetailPanel } from '@/features/checklists/question.detail-panel';
import { DashboardPanel } from '@/features/dashboard/dashboard.panel';
import { DocsDetailsPanel } from '@/features/docs/docs.details-panel';
import { DocsGroupPanel } from '@/features/docs/docs.group-panel';
import { LabelCreatePanel } from '@/features/labels/labels.create-panel';
import { LabelDetailsPanel } from '@/features/labels/labels.details-panel';
import { LabelsPanel } from '@/features/labels/labels.panel';
import { MyHoursPanel } from '@/features/my-hours/my-hours.panel';
import { PanelStateTestPanel } from '@/features/panel-state-test/panel-state-test.panel';
import { ArchivedProjectsPanel } from '@/features/projects/archived-projects.panel';
import { ProjectDetailsPanel } from '@/features/projects/projects.details-panel';
import { ProjectsPanel } from '@/features/projects/projects.panel';
import { SyncStatusPanel } from '@/features/offline/sync-status.panel';
import { SettingsPanel } from '@/features/settings/settings.panel';
import { AllTicketsPanel } from '@/features/tickets/all-tickets.panel';
import { TemplateCreatePanel } from '@/features/templates/templates.create-panel';
import { TemplatesPanel } from '@/features/templates/templates.panel';
import { TicketRedirectPanel } from '@/features/tickets/ticket.redirect-panel';
import { TicketCreatePanel } from '@/features/tickets/tickets.create-panel';
import { TicketDetailsPanel } from '@/features/tickets/tickets.details-panel';
import { LaborDetailPanel } from '@/features/workorders/components/LaborDetailPanel';
import { MaterialDetailPanel } from '@/features/workorders/components/MaterialDetailPanel';
import { DayPlanningPanel } from '@/features/workorders/dayplanning/dayplanning.panel';
import { MyWorkordersPanel } from '@/features/workorders/myworkorders';
import { WorkorderDetailsPanel } from '@/features/workorders/workorders.details-panel';
import { WorkordersPanel } from '@/features/workorders/workorders.panel';
import { SignaturePanel } from '@/features/workorders/workorders.signature-panel';
import { WorkDetailsPanel } from '@/features/works/works.details-panel';
import type { LucideIcon } from 'lucide-react';
import {
  Archive,
  Bug,
  AlertTriangle,
  Building,
  CalendarDays,
  ClipboardCheck,
  Database,
  Clock,
  FileText,
  FolderOpen,
  LayoutDashboard,
  ListTodo,
  MapPin,
  Megaphone,
  Package,
  PenLine,
  Plus,
  RefreshCw,
  Settings,
  Tag,
  TestTube,
  User,
  Users,
  Wrench,
} from 'lucide-react';
import type { AccessMeta } from './navigation/access';
import type { DrillDownNode } from './navigation/types';

// Example meta type for UI/data (can be extended by consumer)
export interface NodeMeta extends AccessMeta {
  label: string;
  icon?: LucideIcon;
  Panel: React.ComponentType<RoutedPanelProps>;
  /**
   * Who may open this node: `'all'` includes external (customer) accounts,
   * which are limited to the docs catalogues and the projects holding them.
   * Omitted = `'internal'`. A rule over the matched params opens only some
   * values of an optional segment. See navigation/access.ts.
   */
  access?: AccessMeta['access'];
  /**
   * Content-aware max width hint for this panel.
   * When the panel is the last (primary) panel, this caps its width so
   * the preceding context panel can absorb remaining space.
   *
   * - 'sm' (max-w-xl  = 576px) — small forms (label detail, material entry)
   * - 'md' (max-w-3xl = 768px) — standard detail views (ticket, account)
   * - 'lg' (max-w-5xl = 1024px) — rich content (docs, checklists)
   * - omitted → unconstrained (tables, dashboards, maps)
   */
  maxPanelWidth?: 'sm' | 'md' | 'lg';
}

// ---------------------------------------------------------------------------
// Docs catalog: recursive routes
// ---------------------------------------------------------------------------
//
// The project /docs tab body renders the catalog root tree; no panel is
// pushed at that level. Drilling into a group or file pushes ONE panel.
// Each level uses a `:groupOrFileId/{group|file}` segment so the own id
// lives in this entry's own params (not the parent's "inverted slot").
// Selection highlight on a parent panel — when a child is currently
// pushed — is resolved by reading the URL beyond the panel's basePath
// (see useStackPath), not from a route slot.
//
// URL examples (relative to /projects/X/docs):
//   /                            (no panel pushed; tree shown in tab body)
//   /100/group                   group(100)
//   /100/group/200/group         group(100) + group(200)
//   /100/group/200/file          group(100) + file(200)
//   /100/file                    file(100)
//
// See apps/frontend/src/app/navigation/matcher.test.ts ('docs recursive
// routes') for the full set of supported paths.

const docsFileChild: DrillDownNode<NodeMeta> = {
  segment: ':groupOrFileId/file',
  label: 'Docs File',
  icon: FileText,
  Panel: DocsDetailsPanel,
  maxPanelWidth: 'lg',
  access: 'all',
};

const docsGroupChild: DrillDownNode<NodeMeta> = {
  segment: ':groupOrFileId/group',
  label: 'Docs Group',
  icon: FileText,
  Panel: DocsGroupPanel,
  maxPanelWidth: 'lg',
  access: 'all',
};
// Self-reference: a group can drill into another group or a file.
docsGroupChild.children = [docsGroupChild, docsFileChild];

const docsTopGroup: DrillDownNode<NodeMeta> = {
  ...docsGroupChild,
  prefix: 'docs',
  label: 'Docs',
};

const docsTopFile: DrillDownNode<NodeMeta> = {
  ...docsFileChild,
  prefix: 'docs',
  label: 'Docs',
};

// ---------------------------------------------------------------------------
// Workorder detail routes
// ---------------------------------------------------------------------------
//
// A workorder can be opened from the global /workorders list and from the
// technician's /my-workorders overview. Both places drill down into the same
// panels, so the detail subtree is defined once and mounted twice. Panels
// build their links from `basePath` so they work under either parent.

/* ???: why cant sign be defined as child of workorder-details along side the
 * tabs? in this case its not a problem to make it a sibling but it should be
 * possible
 */
const workorderRoutes = (
  prefix: string,
): [DrillDownNode<NodeMeta>, DrillDownNode<NodeMeta>] => [
  {
    segment: `${prefix}:workorderId/sign`,
    label: 'Sign & Complete',
    icon: PenLine,
    Panel: SignaturePanel,
    maxPanelWidth: 'sm',
  },
  {
    segment: `${prefix}:workorderId/:?tab`,
    label: 'Workorder Details',
    icon: Wrench,
    Panel: WorkorderDetailsPanel,
    maxPanelWidth: 'md',
    children: [
      {
        prefix: 'labor',
        segment: ':laborId',
        label: 'Labor Entry',
        icon: Clock,
        Panel: LaborDetailPanel,
        maxPanelWidth: 'sm',
      },
      {
        prefix: 'materials',
        segment: ':materialId',
        label: 'Material',
        icon: Package,
        Panel: MaterialDetailPanel,
        maxPanelWidth: 'sm',
      },
    ],
  },
];

/**
 * An optional tab segment where externals may open only `allowed` (or no
 * tab, which the panel then defaults to an allowed one).
 */
const tabAccess =
  (allowed: string[]) =>
  ({ tab }: Record<string, string>): 'internal' | 'all' =>
    !tab || allowed.includes(tab) ? 'all' : 'internal';

// ---------------------------------------------------------------------------
// Project detail routes
// ---------------------------------------------------------------------------
//
// A project opens from the active projects list and from the archive. Each
// keeps its own URL (/projects/:id vs /archived-projects/:id) so the sidebar
// and breadcrumbs stay in the section the user came from. Panels build their
// links from `basePath` so they work under either parent.

const projectDetailsRoute: DrillDownNode<NodeMeta> = {
  segment: ':projectId/:?tab',
  label: 'Project Details',
  icon: FolderOpen,
  Panel: ProjectDetailsPanel,
  // Externals only get the docs tab (the panel defaults to it).
  access: tabAccess(['docs']),
  children: [
    {
      prefix: 'tickets',
      segment: 'new',
      label: 'Create ticket',
      icon: Plus,
      Panel: TicketCreatePanel,
      maxPanelWidth: 'md',
    },
    {
      prefix: 'tickets',
      segment: ':ticketId',
      label: 'Ticket Details',
      icon: ListTodo,
      Panel: TicketDetailsPanel,
      maxPanelWidth: 'md',
    },
    docsTopGroup,
    docsTopFile,
    {
      prefix: 'works',
      segment: ':workId/:?tab',
      label: 'Work Details',
      Panel: WorkDetailsPanel,
      children: [
        {
          prefix: 'tickets',
          segment: 'new',
          label: 'Create Ticket',
          Panel: TicketCreatePanel,
          maxPanelWidth: 'md',
        },
        {
          prefix: 'tickets',
          segment: ':ticketId',
          label: 'Ticket Details',
          Panel: TicketDetailsPanel,
          maxPanelWidth: 'md',
        },
        {
          prefix: 'lists',
          segment: 'new',
          label: 'Create List',
          icon: Plus,
          Panel: ChecklistCreatePanel,
          maxPanelWidth: 'md',
        },
        {
          prefix: 'lists',
          segment: ':checklistId/:?questionId',
          label: 'List Details',
          Panel: ChecklistDetailsPanel,
          maxPanelWidth: 'sm',
          children: [
            {
              segment: 'details',
              label: 'Question Details',
              Panel: QuestionDetailPanel,
              maxPanelWidth: 'sm',
            },
            {
              segment: 'activity',
              label: 'Activity',
              Panel: ChecklistActivityPanel,
              maxPanelWidth: 'sm',
            },
            {
              segment: 'inheritance',
              label: 'Inheritance',
              Panel: InheritancePanel,
              maxPanelWidth: 'sm',
            },
          ],
        },
      ],
    },
  ],
};

// Test and debug screens: development builds only, whoever is signed in.
const devRoutes: DrillDownNode<NodeMeta>[] = [
  {
    segment: 'panel-state-test',
    label: 'Panel State Test',
    icon: TestTube,
    Panel: PanelStateTestPanel,
  },
  {
    segment: 'error',
    label: 'Error Boundary Test',
    icon: AlertTriangle,
    Panel: ErrorTestPanel,
    maxPanelWidth: 'md',
    children: [
      {
        segment: 'error',
        label: 'Nested Error',
        icon: AlertTriangle,
        Panel: ErrorTestPanel,
        maxPanelWidth: 'md',
      },
    ],
  },
  {
    segment: 'auth-debug',
    label: 'Auth Debug',
    icon: Bug,
    Panel: AuthDebugPanel,
  },
];

export const drillDownTree: DrillDownNode<NodeMeta>[] = [
  {
    segment: '/tickets/:ticketId',
    label: 'Ticket redirect',
    Panel: TicketRedirectPanel,
  },
  {
    segment: '/lists/:listId',
    label: 'List redirect',
    Panel: ListRedirectPanel,
  },
  {
    segment: 'all-tickets',
    label: 'All tickets',
    icon: ListTodo,
    Panel: AllTicketsPanel,
    children: [
      {
        segment: ':ticketId',
        label: 'Ticket Details',
        icon: ListTodo,
        Panel: TicketDetailsPanel,
        maxPanelWidth: 'md',
      },
    ],
  },
  {
    segment: 'settings/:?tab',
    label: 'Settings',
    icon: Settings,
    Panel: SettingsPanel,
    maxPanelWidth: 'md',
    access: tabAccess(['personal']),
  },
  {
    // Offline sync is user-agnostic; the indicator in the user menu links
    // here, so externals can see and install it too.
    segment: 'sync-status',
    label: 'Sync status',
    icon: RefreshCw,
    Panel: SyncStatusPanel,
    maxPanelWidth: 'md',
    access: 'all',
  },
  {
    segment: 'cache',
    label: 'Cache',
    icon: Database,
    Panel: CachePanel,
  },
  {
    segment: 'accounts',
    label: 'Accounts',
    icon: Users,
    Panel: AccountsPanel,
    children: [
      {
        segment: ':accountId',
        label: 'Account Details',
        icon: User,
        Panel: AccountDetailsPanel,
        maxPanelWidth: 'md',
      },
    ],
  },
  {
    segment: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    Panel: DashboardPanel,
  },
  {
    segment: 'my-feedback',
    label: 'My feedback',
    icon: Megaphone,
    Panel: FeedbackPanel,
    maxPanelWidth: 'md',
  },
  {
    segment: 'templates',
    label: 'Templates',
    icon: ClipboardCheck,
    Panel: TemplatesPanel,
    children: [
      {
        segment: 'new',
        label: 'Create Template',
        icon: Plus,
        Panel: TemplateCreatePanel,
        maxPanelWidth: 'md',
      },
      {
        segment: ':templateId/:?nodeId',
        label: 'Template Details',
        icon: ClipboardCheck,
        Panel: TemplateDetailsPanel,
        maxPanelWidth: 'sm',
        children: [
          {
            segment: 'details',
            label: 'Question Details',
            Panel: QuestionDetailPanel,
            maxPanelWidth: 'sm',
          },
          {
            segment: 'activity',
            label: 'Activity',
            Panel: ChecklistActivityPanel,
            maxPanelWidth: 'sm',
          },
          {
            segment: 'inheritance',
            label: 'Inheritance',
            Panel: InheritancePanel,
            maxPanelWidth: 'sm',
          },
        ],
      },
    ],
  },
  ...(import.meta.env.DEV ? devRoutes : []),
  {
    segment: 'labels',
    label: 'Labels',
    icon: Tag,
    Panel: LabelsPanel,
    children: [
      {
        segment: 'new',
        label: 'Create Label',
        icon: Plus,
        Panel: LabelCreatePanel,
        maxPanelWidth: 'sm',
      },
      {
        segment: ':labelId',
        label: 'Label Details',
        icon: Tag,
        Panel: LabelDetailsPanel,
        maxPanelWidth: 'sm',
      },
    ],
  },
  {
    segment: 'projects',
    label: 'Projects',
    icon: Building,
    Panel: ProjectsPanel,
    access: 'all',
    children: [projectDetailsRoute],
  },
  {
    segment: 'archived-projects',
    label: 'Archived Projects',
    icon: Archive,
    Panel: ArchivedProjectsPanel,
    // The projects list is active-only by API contract; the archive is the
    // other half, for externals too (scoped by their catalogue grants).
    access: 'all',
    children: [projectDetailsRoute],
  },
  {
    segment: 'workorders',
    label: 'Workorders',
    icon: Wrench,
    Panel: WorkordersPanel,
    children: workorderRoutes(''),
  },
  {
    // Labor rows drill down into the workorder (like my-workorders) so the
    // week overview stays visible.
    segment: 'my-hours/:?week',
    label: 'My Hours',
    icon: Clock,
    Panel: MyHoursPanel,
    maxPanelWidth: 'md',
    children: workorderRoutes('workorders/'),
  },
  {
    // Opening a workorder from the planning drills down like my-workorders,
    // so the planner keeps the day's list and map in view.
    segment: 'day-planning/:?date/:?techId',
    label: 'Day Planning',
    icon: CalendarDays,
    Panel: DayPlanningPanel,
    children: workorderRoutes('workorders/'),
  },
  {
    // Opening a workorder from here drills down (the overview stays visible)
    // instead of jumping to the global /workorders branch. The selected user
    // lives in panel state, not the URL, so the only optional segment is the
    // date and the literal `workorders/` child segment stays unambiguous.
    segment: 'my-workorders/:?date',
    label: 'My Workorders',
    icon: MapPin,
    Panel: MyWorkordersPanel,
    children: workorderRoutes('workorders/'),
  },
  {
    segment: '/',
    label: 'Root',
    Panel: RootRedirectPanel,
    access: 'all',
  },
];
